import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { formatCalendarDate } from 'src/app/shared/utils/date.utils';

interface SprayRecordListItem {
  id: string;
  organizationId: string;
  organizationName: string;
  fieldName: string;
  cropType: string;
  customerName: string | null;
  applicationDate: string;
  status: string;
  totalAcreage: number;
  pilotName: string;
  droneCount: number;
  droneNames: string[];
  chemicals: Array<{
    id: string;
    name: string;
    rate: string | null;
  }>;
}

interface SprayRecordsResponse {
  success: boolean;
  data: {
    records: SprayRecordListItem[];
    total: number;
    page: number;
    pageSize: number;
  };
}

/**
 * Organization Spray Records Tab Component
 *
 * Displays a read-only paginated table of spray records for an organization.
 * Reuses the existing GET /admin/spray-records endpoint with organizationId filter.
 * Row clicks navigate to the flight record detail page with a fromOrg query param.
 */
@Component({
  selector: 'app-organization-spray-records-tab',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './organization-spray-records-tab.component.html',
  styleUrls: ['./organization-spray-records-tab.component.scss']
})
export class OrganizationSprayRecordsTabComponent implements OnInit {
  @Input() organizationId!: string;

  records: any[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalRecords = 0;
  totalPages = 0;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (!this.organizationId) {
      this.errorMessage = 'Organization ID is required';
      this.loading = false;
      return;
    }

    this.loadSprayRecords();
  }

  async loadSprayRecords(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const params = new URLSearchParams({
        organizationId: this.organizationId,
        page: this.currentPage.toString(),
        pageSize: this.pageSize.toString()
      });

      const response = await firstValueFrom(
        this.http.get<SprayRecordsResponse>(
          `${environment.apiUrl}/admin/spray-records?${params.toString()}`
        )
      );

      if (response.success && response.data) {
        this.records = response.data.records;
        this.totalRecords = response.data.total;
        this.totalPages = Math.ceil(this.totalRecords / this.pageSize);
      }
    } catch (error: any) {
      console.error('Failed to load spray records:', error);
      this.errorMessage = 'Failed to load spray records. Please try again.';
    } finally {
      this.loading = false;
    }
  }

  navigateToRecord(record: any): void {
    this.router.navigate(['/flight-records', record.id], {
      queryParams: { fromOrg: this.organizationId }
    });
  }

  onPageChange(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadSprayRecords();
    }
  }

  getStatusBadgeClass(status: string): string {
    switch (status) {
      case 'completed': return 'bg-success';
      case 'pending': return 'bg-warning text-dark';
      case 'cancelled': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  }

  getStatusText(status: string): string {
    switch (status) {
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status;
    }
  }

  /** Calendar-date safe — see shared/utils/date.utils.ts. */
  formatDate(date: string | null): string {
    return formatCalendarDate(date);
  }

  getChemicalNames(chemicals: any[]): string {
    if (!chemicals || chemicals.length === 0) return '-';
    return chemicals.map(c => c.name).join(', ');
  }

  getDroneNames(droneNames: string[]): string {
    if (!droneNames || droneNames.length === 0) return '-';
    return droneNames.join(', ');
  }

  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  get showingStart(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get showingEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRecords);
  }
}
